import { useState, useEffect, useRef } from 'react'
import { Files, Folder, File, Download, Loader2, AlertCircle, Monitor, ChevronDown, ChevronUp, Package, Trash2, Zap, Clock, Shield, Archive } from 'lucide-react'
import { nasAPI, authAPI } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'

import PageHeader from '@/components/PageHeader'

/* Session-cache voor folder-lijst zodat page niet leeg voelt bij nav-back */
const FILES_CACHE_KEY = 'files-folders-cache-v1'
const _readFolderCache = () => {
  try { return JSON.parse(sessionStorage.getItem(FILES_CACHE_KEY) || 'null') } catch { return null }
}
const _writeFolderCache = (folders, smb) => {
  try { sessionStorage.setItem(FILES_CACHE_KEY, JSON.stringify({ folders, smb, ts: Date.now() })) } catch {}
}

const formatSize = (bytes) => {
  if (!bytes) return '–'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function FileCard({ folder, isAdmin, cacheInfo, onCacheRefresh, onStartPolling }) {
  const [expanded, setExpanded] = useState(false)
  const [files, setFiles] = useState([])
  const [loadingFiles, setLoadingFiles] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState(null)
  const [clearingCache, setClearingCache] = useState(false)
  const [precaching, setPrecaching] = useState(false)

  const folderSafeName = folder.name.replace(/[^a-zA-Z0-9-_.]/g, '_')
  const cached = cacheInfo?.cached?.find(c => c.name === folderSafeName)
  const isGenerating = cacheInfo?.generating?.includes(folder.path)

  const loadFiles = async () => {
    setLoadingFiles(true)
    try {
      const res = await nasAPI.browseSharedFiles(folder.path)
      setFiles(res.data.items || [])
    } catch {
      setFiles([])
    } finally {
      setLoadingFiles(false)
    }
  }

  const toggleExpand = () => {
    if (!expanded) loadFiles()
    setExpanded(!expanded)
  }

  const handleDownloadFolder = async () => {
    setDownloading(true)
    setDownloadError(null)
    try {
      const token = localStorage.getItem('auth_token')
      const url = `/api/nas/files/download-folder?path=${encodeURIComponent(folder.path)}`
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })

      if (res.status === 503) {
        const data = await res.json()
        setDownloadError(data.error)
        return
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setDownloadError(data.error || `HTTP ${res.status}`)
        return
      }

      const blob = await res.blob()
      const objectUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = `${folder.name}.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(objectUrl)
      onCacheRefresh()
    } catch (e) {
      setDownloadError(`Download mislukt: ${e.message}`)
    } finally {
      setDownloading(false)
    }
  }

  const handlePrecache = async () => {
    setPrecaching(true)
    try {
      await nasAPI.precacheSharedFolder(folder.path)
      await onCacheRefresh()
      onStartPolling()
    } catch {
      alert('Pre-cache starten mislukt')
    } finally {
      setPrecaching(false)
    }
  }

  const handleClearCache = async () => {
    if (!confirm(`Cache van "${folder.name}" wissen?`)) return
    setClearingCache(true)
    try {
      await nasAPI.clearSharedCache(folder.path)
      onCacheRefresh()
    } catch {
      alert('Cache wissen mislukt')
    } finally {
      setClearingCache(false)
    }
  }

  const handleDownloadFile = async (file) => {
    try {
      const token = localStorage.getItem('auth_token')
      const url = `/api/nas/files/download?path=${encodeURIComponent(file.path)}`
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const blob = await res.blob()
      const objectUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = file.name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(objectUrl)
    } catch (e) {
      alert(`Download mislukt: ${e.message}`)
    }
  }

  return (
    <div className="rounded-lg border border-neon bg-card overflow-hidden shadow-lg flex flex-col">
      <div className="p-5 flex flex-col gap-3">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-neon-green/10 flex items-center justify-center">
            <Package className="h-5 w-5 text-neon-green" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-lg leading-tight truncate">{folder.name}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              {isGenerating ? (
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">
                  <Loader2 className="h-3 w-3 animate-spin mr-1" /> Wordt gegenereerd...
                </Badge>
              ) : cached ? (
                <Badge className="bg-neon-green/20 text-neon-green border-neon-green/30 text-xs">
                  <Zap className="h-3 w-3 mr-1" /> Gecached · {formatSize(cached.size)}
                </Badge>
              ) : (
                <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs">
                  <Clock className="h-3 w-3 mr-1" /> Eerste download duurt even
                </Badge>
              )}
            </div>
          </div>
        </div>

        {downloadError && (
          <p className="text-xs text-red-400 bg-red-500/10 rounded px-2 py-1">{downloadError}</p>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            onClick={handleDownloadFolder}
            disabled={downloading || isGenerating}
            className="flex-1 bg-neon-green/10 hover:bg-neon-green/20 text-neon-green border border-neon-green/30 shadow-neon-green hover:shadow-neon-green-lg transition-all disabled:opacity-40"
          >
            {downloading
              ? <><Loader2 className="h-4 w-4 animate-spin" />{cached ? ' Downloaden...' : ' Genereren & downloaden...'}</>
              : <><Download className="h-4 w-4" /> Download als ZIP</>
            }
          </Button>

          {isAdmin && !cached && !isGenerating && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePrecache}
              disabled={precaching}
              className="text-muted-foreground hover:text-neon-blue hover:bg-neon-blue/10"
              title="Pre-cache ZIP genereren"
            >
              {precaching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
            </Button>
          )}

          {isAdmin && cached && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClearCache}
              disabled={clearingCache}
              className="text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
              title="Cache wissen"
            >
              {clearingCache ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={toggleExpand}
            className="text-muted-foreground hover:text-foreground"
            title="Bekijk bestanden"
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Expanded file list */}
      {expanded && (
        <div className="border-t border-border/50 bg-background/30">
          {loadingFiles ? (
            <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Laden...
            </div>
          ) : files.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">Geen bestanden gevonden</div>
          ) : (
            <ul className="divide-y divide-border/30">
              {files.map((item) => (
                <li key={item.path} className="flex items-center gap-3 px-5 py-2.5 hover:bg-neon-green/5 group">
                  {item.type === 'dir'
                    ? <Folder className="h-4 w-4 text-yellow-400 flex-shrink-0" />
                    : <File className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  }
                  <span className="flex-1 text-sm truncate">{item.name}</span>
                  {item.type === 'file' && (
                    <>
                      <span className="text-xs text-muted-foreground flex-shrink-0">{formatSize(item.size)}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDownloadFile(item)}
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 text-neon-green hover:bg-neon-green/10 transition-all"
                      >
                        <Download className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                  {item.type === 'dir' && (
                    <Badge variant="outline" className="text-xs py-0 border-yellow-400/40 text-yellow-400">map</Badge>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

export default function FilesPage() {
  const [folders, setFolders] = useState(() => (_readFolderCache()?.folders) || [])
  const [smbPath, setSmbPath] = useState('')
  const [cacheInfo, setCacheInfo] = useState({ cached: [], generating: [] })
  const [isLoading, setIsLoading] = useState(() => !_readFolderCache())
  const [error, setError] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)
  const [openingAdmin, setOpeningAdmin] = useState(false)

  const pollRef = useRef(null)

  const fetchCacheInfo = async () => {
    try {
      const res = await nasAPI.getSharedCacheInfo()
      setCacheInfo(res.data)
      return res.data
    } catch {}
  }

  const startPolling = () => {
    if (pollRef.current) return
    pollRef.current = setInterval(async () => {
      const data = await fetchCacheInfo()
      if (!data || data.generating?.length === 0) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }, 5000)
  }

  useEffect(() => () => clearInterval(pollRef.current), [])

  useEffect(() => {
    const load = async () => {
      try {
        const [res, userRes] = await Promise.all([
          nasAPI.browseSharedFiles(''),
          authAPI.getCurrentUser().catch(() => ({ data: { user: null } }))
        ])
        const dirs = (res.data.items || []).filter(i => i.type === 'dir')
        setFolders(dirs)
        setSmbPath(res.data.smb || '')
        _writeFolderCache(dirs, res.data.smb || '')
        setCurrentUser(userRes.data.user)
        await fetchCacheInfo()
      } catch (err) {
        setError(err.response?.data?.error || 'Kan bestanden niet laden van de NAS')
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [])

  const isAdmin = ['admin','superadmin'].includes(currentUser?.role)
  const cachedCount = cacheInfo.cached?.length || 0

  const openSmbAdmin = () => {
    setOpeningAdmin(true)
    const token = localStorage.getItem('auth_token')
    const host = window.location.hostname
    const content = `[InternetShortcut]\r\nURL=file:////${host}/shared_files\r\n`
    const blob = new Blob([content], { type: 'application/octet-stream' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'shared_files_admin.url'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    setTimeout(() => setOpeningAdmin(false), 1500)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-neon-green to-neon-blue flex items-center justify-center flex-shrink-0">
            <Files className="h-6 w-6 text-white" />
          </div>
          <div>
            <PageHeader icon={Files} title="Gedeelde Bestanden" />
            <p className="text-muted-foreground text-sm">Download bestanden van de NAS</p>
          </div>
        </div>
        {cachedCount > 0 && (
          <Badge className="bg-neon-green/20 text-neon-green border-neon-green/30">
            <Zap className="h-3 w-3 mr-1" /> {cachedCount} gecached
          </Badge>
        )}
      </div>

      {/* Admin SMB link */}
      {smbPath && isAdmin && (
        <div className="p-4 rounded-lg border border-orange-500/20 bg-orange-500/5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Shield className="h-4 w-4 text-orange-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-orange-400">Admin toegang · NAS Verkenner</p>
              <p className="text-xs text-muted-foreground truncate">Opent map met schrijfrechten via <code>{smbPath}</code></p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={openSmbAdmin}
            disabled={openingAdmin}
            className="shrink-0 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 border border-orange-500/30"
          >
            {openingAdmin
              ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Openen...</>
              : <><Monitor className="h-3 w-3 mr-1" /> Open als Admin</>}
          </Button>
        </div>
      )}

      {error && (
        <Alert variant="destructive" className="border-red-500/50 bg-red-500/10">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-neon-green" />
          <span className="ml-3 text-muted-foreground">Laden van NAS...</span>
        </div>
      )}

      {!isLoading && !error && folders.length === 0 && (
        <div className="rounded-lg border border-neon bg-card/50 text-center py-16">
          <Files className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
          <h3 className="text-xl font-semibold mb-2">Geen mappen gevonden</h3>
          <p className="text-muted-foreground text-sm">
            Zet bestanden in mappen op de NAS share <code className="text-neon-green">{smbPath}</code>
          </p>
        </div>
      )}

      {!isLoading && folders.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {folders.map((folder) => (
            <FileCard
              key={folder.path}
              folder={folder}
              isAdmin={isAdmin}
              cacheInfo={cacheInfo}
              onCacheRefresh={fetchCacheInfo}
              onStartPolling={startPolling}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/* Skeleton placeholders tijdens initial load */
function SkeletonCard() {
  return (
    <div className="rounded-md border border-border bg-card/60 p-4 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded bg-muted" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-2/3 bg-muted rounded" />
          <div className="h-3 w-1/3 bg-muted rounded" />
        </div>
      </div>
    </div>
  )
}
